import * as React from 'react'
import { useEffect } from 'react'
import { ProjectInfo } from '../types/Project/ProjectInfo'
import './styles.scss'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Box, IconButton, Tab, Tabs, TextField, Typography } from '@mui/material'
import Breadcrumbs from '../components/navigation/Breadcrumbs'
import { DatePicker } from '@mui/x-date-pickers'
import { useShiftEndContext } from '../components/reports/shift-end/ShiftEndContext'
import { useTokenClaims } from '../security/useTokenClaims'
import dayjs, { Dayjs } from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import { useProjectFeaturesContext } from '../components/project/contexts/ProjectFeaturesContext'
import { useIndexedDB } from '../components/hooks/useIndexedDB'
import { checkTimesheetDate } from '../services/OfflineServices/foremanCacheService'
import { showErrorToast } from '../services/CommonServices/alertServices'
import { useConnectionContext } from '../components/navigation/ConnectionContext'
import CreateTimesheetButton from '../components/reports/shift-end/workersummary/manualTimesheet/CreateTimesheetButton'

dayjs.extend(timezone)

function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		'aria-controls': `simple-tabpanel-${index}`,
	}
}

const TimeTabs = ({ project }: { project: ProjectInfo | undefined }) => {
	const [value, setValue] = React.useState(0)

	const navigate = useNavigate()
	const location = useLocation()

	useEffect(() => {
		if (!location.pathname.includes('workers') && !location.pathname.includes('workitems')) {
			navigate('workers')
		}
		// if on files tab and click menu for project it should go back to config tab, this detects nav change and sets the tab flag accordingly
		location.pathname.includes('workitems') ? setValue(1) : setValue(0)
	}, [location])

	const handleChange = (_: React.SyntheticEvent, newValue: number) => {
		setValue(newValue)
	}

	return (
		<Box sx={{ width: '100%' }}>
			<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
				<Tabs value={value} onChange={handleChange} aria-label='basic tabs example'>
					<Tab
						color='#ff000'
						label='Worker Summary'
						sx={(theme) => ({
							color: theme.palette.info.main,
							'&:selected': theme.palette.info.main,
						})}
						{...a11yProps(0)}
						onClick={() => navigate('workers')}
					/>
					<Tab
						color='info'
						label='Work Items Progress'
						sx={(theme) => ({
							color: theme.palette.info.main,
							'&:selected': theme.palette.info.main,
						})}
						{...a11yProps(1)}
						onClick={() => navigate('workitems')}
					/>
				</Tabs>
			</Box>
			<Outlet context={project} />
		</Box>
	)
}

const ShiftEndReportPage = () => {
	const { shiftDate, setShiftDate, offlineAvailableDate } = useShiftEndContext()
	const { role } = useTokenClaims()
	const { lastDirectReport } = useShiftEndContext()
	const { projectTimeZone, isFeatureEnabled } = useProjectFeaturesContext()
	const navigate = useNavigate()
	const db = useIndexedDB()
	const { connectionStatus } = useConnectionContext()
	const handleDateChange = async (date: Dayjs | null) => {
		if (date) {
			if (db && !(await checkTimesheetDate(db, date, connectionStatus))) {
				showErrorToast({
					message:
						'Timesheet access is limited to the day when no internet connection is available',
				})
				return
			}
			const utcDate = date.tz(projectTimeZone || 'UTC').utc()
			setShiftDate(utcDate.toDate())
		}
	}

	return (
		<>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
				<Box>
					<Breadcrumbs />
					<Box display={'flex'}>
						{role.title !== 'Foreman' && (
							<IconButton
								color='default'
								onClick={() => {
									navigate(`/reports/${lastDirectReport() ?? ''}`)
								}}
							>
								<ArrowBackIcon sx={{ height: '2rem', width: 'auto' }} />
							</IconButton>
						)}
						<Typography
							sx={{
								fontSize: '1.5rem',
								paddingLeft: '1rem',
								paddingTop: '.5rem',
								zIndex: '3',
							}}
						>
							Shift-End Report
						</Typography>
					</Box>
				</Box>
				<Box sx={{ marginRight: '1rem', display: 'flex', gap: '1rem' }}>
					{isFeatureEnabled(14) && <CreateTimesheetButton />}
					<DatePicker
						label='Pick a date'
						value={shiftDate}
						onChange={handleDateChange}
						renderInput={(props) => <TextField {...props} />}
						shouldDisableDate={(date) => {
							if (connectionStatus === 'online') {
								return false
							}
							return !dayjs(offlineAvailableDate).isSame(date, 'day')
						}}
					/>
				</Box>
			</Box>
			<TimeTabs project={undefined} />
		</>
	)
}

export default ShiftEndReportPage
